import type { CompanyNameSuggestion, DiscoveryView } from "@scoach/types";
import { Button, Spinner } from "@scoach/ui";
import { Search, Spark } from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { CompanyPicker } from "../companies/CompanyPicker.tsx";
import { companiesApi } from "../companies/api.ts";
import { CompanyLogo } from "./CompanyLogo.tsx";
import { discoveryApi } from "./api.ts";

/** Entry screen for the Discovery workspace: pick a company (name + logo) to
 * research — the picker disambiguates as you type — and reopen recent discoveries. */
export function DiscoveryEntry() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [suggestions, setSuggestions] = useState<CompanyNameSuggestion[]>([]);
  const [recent, setRecent] = useState<DiscoveryView[]>([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    companiesApi
      .list()
      .then(setSuggestions)
      .catch(() => {});
    discoveryApi
      .list()
      .then(setRecent)
      .catch(() => {});
  }, []);

  async function createAndGo(company: string, website?: string) {
    if (creating) return;
    setCreating(true);
    try {
      const { id } = await discoveryApi.create(
        website ? { name: company, website } : { name: company },
      );
      nav({ to: "/discovery/$id", params: { id } });
    } finally {
      setCreating(false);
    }
  }

  function begin() {
    const q = name.trim();
    if (!q) return;
    const isUrl = /\.[a-z]{2,}$/i.test(q) && !q.includes(" ");
    void createAndGo(q, isUrl ? q : undefined);
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Search size={20} />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--text-1)" }}>
          Discovery
        </h1>
      </div>
      <p style={{ fontSize: 14, color: "var(--text-3)", marginTop: 0 }}>
        Deep-research any company before your meeting — founders, news, competitors, market, hiring,
        and where Google Cloud fits. Start typing and pick the right company from the list.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 16 }}>
        <div style={{ flex: 1 }}>
          <CompanyPicker
            value={name}
            onChange={setName}
            onSelect={(pick) => void createAndGo(pick.name, pick.domain)}
            priorSuggestions={suggestions}
            placeholder="Company name or website (e.g. Wix or wix.com)"
            autoFocus
          />
        </div>
        <Button onClick={begin} disabled={creating || !name.trim()}>
          {creating ? (
            <>
              <Spinner size={14} /> Starting…
            </>
          ) : (
            <>
              <Spark size={14} /> Research
            </>
          )}
        </Button>
      </div>

      {recent.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-3)", marginBottom: 10 }}>
            Recent discoveries
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {recent.map((d) => (
              <button
                type="button"
                key={d.discovery.id}
                onClick={() => nav({ to: "/discovery/$id", params: { id: d.discovery.id } })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: 12,
                  border: "1px solid var(--border-soft)",
                  borderRadius: 12,
                  background: "var(--surface)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <CompanyLogo
                  name={d.discovery.companyName}
                  domain={d.profile?.website ?? d.discovery.website}
                  logoUrl={d.profile?.logoUrl}
                  size={36}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "var(--text-1)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {d.discovery.companyName}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text-4)" }}>
                    {d.profile ? "Ready" : "Researching…"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
